import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";

const API_URL = process.env.REACT_APP_API_URL;

// Main App Component with Router
function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/recipe-list" element={<ProtectedRecipeList />} />
        <Route path="/confirmation" element={<ConfirmationPage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

// Home Page Component
function HomePage() {
  const loadRecipes = () => {
    // This function can be passed to RecipeForm if needed for refreshing
    console.log("Recipe added - would refresh if needed");
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      <h1 className="text-3xl font-bold text-center text-gray-800 mb-8">
        Recipe Gift Book
      </h1>
      <p className="text-2xl text-center text-gray-600 mb-8">
        Add your recipe gift
      </p>
      <RecipeForm loadRecipes={loadRecipes} />
    </div>
  );
}

// Password Protection Component
function ProtectedRecipeList() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [recipes, setRecipes] = useState([]);
  const navigate = useNavigate();

  const CORRECT_PASSWORD = "abcd12345!";

  // Load recipes when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      loadRecipes();
    }
  }, [isAuthenticated]);

  const loadRecipes = async () => {
    try {
      const response = await fetch(API_URL + "/api/recipes/");

      if (!response.ok) {
        throw new Error("Failed to fetch recipes");
      }

      const recipes = await response.json();
      setRecipes(recipes);
    } catch (error) {
      console.error("Error loading recipes:", error);
    }
  };

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (password === CORRECT_PASSWORD) {
      setIsAuthenticated(true);
      setError("");
    } else {
      setError("Incorrect password. Please try again.");
      setPassword("");
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-md">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h1 className="text-2xl font-bold text-center text-gray-800 mb-6">
            Recipe List Access
          </h1>
          <p className="text-gray-600 text-center mb-6">
            Please enter the password to view the recipe list.
          </p>
          <form onSubmit={handlePasswordSubmit}>
            <div className="mb-4">
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>
            {error && (
              <div className="text-red-500 text-sm mb-4 text-center">
                {error}
              </div>
            )}
            <button
              type="submit"
              className="w-full bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded-md transition-colors"
            >
              Access Recipe List
            </button>
          </form>
          <div className="mt-4 text-center">
            <button
              onClick={() => navigate("/")}
              className="text-blue-500 hover:text-blue-700 text-sm underline"
            >
              Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold text-gray-800">
          Recipe List
        </h1>
        <button
          onClick={() => navigate("/")}
          className="bg-gray-500 hover:bg-gray-600 text-white font-medium py-2 px-4 rounded-md transition-colors"
        >
          Back to Home
        </button>
      </div>
      <RecipeList recipes={recipes} />
    </div>
  );
}

// Confirmation Page Component
function ConfirmationPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const recipe = location.state?.recipe;

  // If no recipe data is available, redirect to home
  if (!recipe) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="text-center mb-8">
        <div className="mb-6">
          <svg
            className="mx-auto text-green-500"
            style={{ maxWidth: "200px", maxHeight: "200px", width: "200px", height: "200px" }}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
        </div>
        <h1 className="text-3xl font-bold text-gray-800 mb-4">
          Thank You!
        </h1>
        <p className="text-xl text-gray-600 mb-8">
          Your recipe is now part of our Anniversary Recipe Collection. Our stomachs are already growling in anticipation.
        </p>
      </div>

      <div className="mb-8">
        <h2 className="text-2xl font-semibold text-gray-800 mb-4 text-center">
          Your Submitted Recipe
        </h2>
        <RecipeCard recipe={recipe} />
      </div>

      <div className="text-center">
        <button
          onClick={() => navigate("/")}
          className="bg-blue-500 hover:bg-blue-600 text-white font-medium py-3 px-6 rounded-md transition-colors"
        >
          Add Another Recipe
        </button>
      </div>
    </div>
  );
}

// Recipe Form Component
function RecipeForm({ loadRecipes }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    author: "",
    notes: "",
    imageFile: null,
    imagePreview: null,
    ingredients: [{ quantity: "", name: "" }],
    instructions: [{ text: "" }],
  });
  const [errors, setErrors] = useState({
    title: "",
    author: "",
    image: "",
    ingredients: "",
    instructions: "",
  });
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showUploadProgress, setShowUploadProgress] = useState(false);

  // Handle input changes for basic fields
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  // Handle image file selection
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setFormData({
          ...formData,
          imageFile: file,
          imagePreview: e.target.result,
        });
      };
      reader.readAsDataURL(file);
    } else {
      setFormData({
        ...formData,
        imageFile: null,
        imagePreview: null,
      });
    }
  };

  // Handle ingredient changes
  const handleIngredientChange = (index, field, value) => {
    const updatedIngredients = [...formData.ingredients];
    updatedIngredients[index] = {
      ...updatedIngredients[index],
      [field]: value,
    };
    setFormData({
      ...formData,
      ingredients: updatedIngredients,
    });
  };

  // Handle instruction changes
  const handleInstructionChange = (index, value) => {
    const updatedInstructions = [...formData.instructions];
    updatedInstructions[index] = {
      ...updatedInstructions[index],
      text: value,
    };
    setFormData({
      ...formData,
      instructions: updatedInstructions,
    });
  };

  // Add new ingredient
  const addIngredient = () => {
    setFormData({
      ...formData,
      ingredients: [...formData.ingredients, { quantity: "", name: "" }],
    });
  };

  // Remove ingredient
  const removeIngredient = (index) => {
    const updatedIngredients = [...formData.ingredients];
    updatedIngredients.splice(index, 1);
    setFormData({
      ...formData,
      ingredients: updatedIngredients,
    });
  };

  // Add new instruction
  const addInstruction = () => {
    setFormData({
      ...formData,
      instructions: [...formData.instructions, { text: "" }],
    });
  };

  // Remove instruction
  const removeInstruction = (index) => {
    const updatedInstructions = [...formData.instructions];
    updatedInstructions.splice(index, 1);
    setFormData({
      ...formData,
      instructions: updatedInstructions,
    });
  };


  // Validate form
  const validateForm = () => {
    let valid = true;
    const newErrors = {
      title: "",
      author: "",
      image: "",
      ingredients: "",
      instructions: "",
    };

    // Check title
    if (!formData.title.trim()) {
      newErrors.title = "Recipe title is required";
      valid = false;
    }

    // Check author
    if (!formData.author.trim()) {
      newErrors.author = "Author name is required";
      valid = false;
    }

    // Check ingredients
    const validIngredients = formData.ingredients.filter(
      (ing) => ing.quantity.trim() && ing.name.trim()
    );
    if (validIngredients.length === 0) {
      newErrors.ingredients = "At least one ingredient is required";
      valid = false;
    }

    // Check instructions
    const validInstructions = formData.instructions.filter((inst) =>
      inst.text.trim()
    );
    if (validInstructions.length === 0) {
      newErrors.instructions = "At least one instruction step is required";
      valid = false;
    }

    setErrors(newErrors);
    return valid;
  };

  // Upload file to S3
  const uploadFileToS3 = async (file) => {
    try {
      const fileExtension = file.name.split(".").pop();
      const contentType = file.type;

      // Get presigned URL from the server
      const presignedUrlResponse = await fetch(
        API_URL + "/api/presigned-upload-url/",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            file_extension: fileExtension,
            content_type: contentType,
          }),
        }
      );

      if (!presignedUrlResponse.ok) {
        throw new Error("Failed to get presigned URL");
      }

      const presignedUrlData = await presignedUrlResponse.json();
      const { presigned_url } = presignedUrlData;

      // Upload file to S3 using the presigned URL
      setShowUploadProgress(true);

      // Use XMLHttpRequest for upload progress tracking
      return new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.upload.addEventListener("progress", (event) => {
          if (event.lengthComputable) {
            const percentComplete = Math.round(
              (event.loaded / event.total) * 100
            );
            setUploadProgress(percentComplete);
          }
        });

        xhr.addEventListener("load", () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            // Construct the public URL for the uploaded file
            const publicUrl = `https://photo-bingo-game-submissions.s3.us-east-2.amazonaws.com/${presignedUrlData.s3_key}`;
            resolve(publicUrl);
          } else {
            reject(new Error("File upload failed"));
          }
        });

        xhr.addEventListener("error", () => {
          reject(new Error("File upload failed"));
        });

        xhr.open("PUT", presigned_url);
        xhr.setRequestHeader("Content-Type", contentType);
        xhr.send(file);
      });
    } catch (error) {
      console.error("Error uploading file:", error);
      throw error;
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form
    const isValid = validateForm();
    if (!isValid) return;

    setLoading(true);

    try {
      // Upload image if provided
      let imageUrl = null;
      if (formData.imageFile) {
        try {
          imageUrl = await uploadFileToS3(formData.imageFile);
        } catch (error) {
          setErrors({
            ...errors,
            image: "Failed to upload image. Please try again.",
          });
          setLoading(false);
          return;
        }
      }

      // Prepare recipe data
      const recipeData = {
        title: formData.title,
        author: formData.author,
        image_url: imageUrl,
        notes: formData.notes || null,
        ingredients: formData.ingredients
          .filter((ing) => ing.quantity.trim() && ing.name.trim())
          .map((ing) => ({ quantity: ing.quantity, name: ing.name })),
        instruction_steps: formData.instructions
          .filter((inst) => inst.text.trim())
          .map((inst) => ({ instruction: inst.text })),
      };

      // Send request to API
      const response = await fetch(API_URL + "/api/recipes/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(recipeData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(JSON.stringify(errorData));
      }

      const savedRecipe = await response.json();

      // Navigate to confirmation page with the recipe data
      navigate("/confirmation", {
        state: {
          recipe: {
            ...savedRecipe,
            created_at: new Date().toISOString() // Add current date if not returned from API
          }
        }
      });

      // Reload recipes
      loadRecipes();
    } catch (error) {
      console.error("Error submitting recipe:", error);
      alert("An error occurred while saving the recipe. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="flex flex-col md:flex-row gap-8">
        <div className="w-full md:w-3/5">
          <form
            className="bg-blue-50 rounded-lg shadow-md p-6"
            onSubmit={handleSubmit}
          >
            <div className="mb-6">
              <label
                htmlFor="author"
                className="block font-semibold text-3xl mb-2 text-gray-700"
              >
                Your Name
              </label>
              <input
                type="text"
                id="author"
                name="author"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 mb-4"
                value={formData.author}
                onChange={handleInputChange}
                required
              />
              {errors.author && (
                <div className="text-red-500 text-sm mt-1">{errors.author}</div>
              )}
              <label
                htmlFor="title"
                className="block font-semibold text-3xl mb-2 text-gray-700"
              >
                Recipe Title
              </label>
              <input
                type="text"
                id="title"
                name="title"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={formData.title}
                onChange={handleInputChange}
                required
              />
              {errors.title && (
                <div className="text-red-500 text-sm mt-1">{errors.title}</div>
              )}
            </div>

            <div className="block font-bold mb-2 text-gray-700">
              Ingredients
            </div>
            <div className="mb-4">
              {formData.ingredients.map((ingredient, index) => (
                <div
                  className="flex gap-3 mb-3 items-end"
                  key={`ingredient-${index}`}
                >
                  <div className="w-1/3">
                    <label
                      htmlFor={`ingredient-quantity-${index + 1}`}
                      className="block text-sm font-medium mb-1"
                    >
                      Quantity
                    </label>
                    <input
                      type="text"
                      id={`ingredient-quantity-${index + 1}`}
                      placeholder=""
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={ingredient.quantity}
                      onChange={(e) =>
                        handleIngredientChange(
                          index,
                          "quantity",
                          e.target.value
                        )
                      }
                    />
                  </div>
                  <div className="flex-1">
                    <label
                      htmlFor={`ingredient-name-${index + 1}`}
                      className="block text-sm font-medium mb-1"
                    >
                      Ingredient
                    </label>
                    <input
                      type="text"
                      id={`ingredient-name-${index + 1}`}
                      placeholder=""
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={ingredient.name}
                      onChange={(e) =>
                        handleIngredientChange(index, "name", e.target.value)
                      }
                    />
                  </div>
                  <button
                    type="button"
                    className="text-red-600 px-3 py-2 rounded-md text-sm cursor-pointer"
                    onClick={() => removeIngredient(index)}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="size-6"
                    >
                      {" "}
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="m9.75 9.75 4.5 4.5m0-4.5-4.5 4.5M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                      />{" "}
                    </svg>
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="text-gray-500 rounded-md text-sm cursor-pointer"
                onClick={addIngredient}
              >
                + Add Ingredient
              </button>
              {errors.ingredients && (
                <div className="text-red-500 text-sm mt-2">
                  {errors.ingredients}
                </div>
              )}
            </div>

            <div className="block font-bold mb-2 text-gray-700">
              Instructions
            </div>
            <div className="mb-6">
              {formData.instructions.map((instruction, index) => (
                <div
                  className="flex gap-3 mb-3 items-start"
                  key={`instruction-${index}`}
                >
                  <div className="flex-1">
                    <label
                      htmlFor={`instruction-${index + 1}`}
                      className="block text-sm font-medium mb-1"
                    >
                      Step {index + 1}
                    </label>
                    <textarea
                      id={`instruction-${index + 1}`}
                      rows="2"
                      placeholder=""
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      value={instruction.text}
                      onChange={(e) =>
                        handleInstructionChange(index, e.target.value)
                      }
                    ></textarea>
                  </div>
                  <button
                    type="button"
                    className="text-red-600 flex px-3 py-5 mt-6 rounded-md text-sm cursor-pointer"
                    onClick={() => removeInstruction(index)}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                      className="size-6"
                    >
                      {" "}
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="m9.75 9.75 4.5 4.5m0-4.5-4.5 4.5M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"
                      />{" "}
                    </svg>
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="text-gray-500 rounded-md text-sm cursor-pointer"
                onClick={addInstruction}
              >
                + Add Instruction
              </button>
              {errors.instructions && (
                <div className="text-red-500 text-sm mt-2">
                  {errors.instructions}
                </div>
              )}
            </div>
            <div>
              <label
                htmlFor="image-file"
                className="block font-bold mb-2 text-gray-700"
              >
                Recipe Image
              </label>
              <div className="mt-2 flex justify-center rounded-lg border border-dashed border-gray-900/25 px-6 py-10">
                <div className="text-center">
                  <div className="mt-4 flex text-sm/6 text-gray-600">
                    <label
                      htmlFor="image-file"
                      className="relative cursor-pointer rounded-md bg-blue-50 font-semibold focus-within:ring-2 "
                    >
                      <div className="bg-blue-50 text-gray-700">
                        Upload a file
                      </div>
                      <input
                        type="file"
                        id="image-file"
                        name="image_file"
                        accept="image/*"
                        className="sr-only"
                        onChange={handleImageChange}
                      />
                      {formData.imagePreview && (
                        <img
                          className="mt-3 max-w-xs rounded-md"
                          src={formData.imagePreview}
                          alt="Recipe preview"
                        />
                      )}
                      {showUploadProgress && (
                        <div className="mt-2">
                          <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-blue-500"
                              style={{ width: `${uploadProgress}%` }}
                            ></div>
                          </div>
                          <div className="text-xs text-gray-600 mt-1">
                            {uploadProgress}%
                          </div>
                        </div>
                      )}
                      {errors.image && (
                        <div className="text-red-500 text-sm mt-1">
                          {errors.image}
                        </div>
                      )}
                    </label>
                  </div>
                </div>
              </div>

              <label
                htmlFor="notes"
                className="block font-bold mb-1 mt-4 text-gray-700"
              >
                Notes
              </label>
              <textarea
                id="notes"
                name="notes"
                rows="3"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={formData.notes}
                onChange={handleInputChange}
              ></textarea>
            </div>

            <button
              type="submit"
              className={`w-full py-3 cursor-pointer rounded-md text-white font-medium text-base mt-4 ${
                loading
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-blue-500 hover:bg-blue-600"
              }`}
              disabled={loading}
            >
              {loading ? "Saving..." : "Save Recipe"}
            </button>
          </form>
        </div>

        <div className="w-full md:w-2/5 mt-6 md:mt-0">
          <div className="sticky top-6">
            <div className="font-bold mb-3 text-lg text-gray-700">
              Live Preview
            </div>
            <RecipePreview formData={formData} />
          </div>
        </div>
      </div>

      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}
    </div>
  );
}

// Recipe Preview Component
function RecipePreview({ formData }) {
  const currentDate = new Date().toLocaleDateString();

  return (
    <div className="bg-gray-50 rounded-lg shadow-md p-4">
      <div className="flex justify-between items-start">
        <div>
          <h3 className="text-xl font-semibold text-gray-800">
            {formData.title || "Recipe Title"}
          </h3>
          <div className="text-sm text-gray-500 mb-2">
            By <span>{formData.author || "Author Name"}</span> | Created on{" "}
            <span>{currentDate}</span>
          </div>
          {formData.notes && (
            <p className="text-gray-700 mb-3">{formData.notes}</p>
          )}
        </div>
        {formData.imagePreview && (
          <img
            src={formData.imagePreview}
            alt="Recipe"
            className="w-24 h-24 object-cover rounded-md"
          />
        )}
      </div>
      <div className="flex flex-col sm:flex-row gap-6 mt-4">
        <div className="flex-1">
          <h4 className="bg-gray-200 px-3 py-1 rounded-md font-medium mb-2">
            Ingredients
          </h4>
          <ul className="list-disc pl-5 space-y-1">
            {formData.ingredients.some((ing) => ing.quantity || ing.name) ? (
              formData.ingredients.map(
                (ing, index) =>
                  (ing.quantity || ing.name) && (
                    <li key={`preview-ing-${index}`} className="text-gray-700">
                      {ing.quantity} {ing.name}
                    </li>
                  )
              )
            ) : (
              <li className="text-gray-500 italic">
                Add ingredients to see them here
              </li>
            )}
          </ul>
        </div>
        <div className="flex-1">
          <h4 className="bg-gray-200 px-3 py-1 rounded-md font-medium mb-2">
            Instructions
          </h4>
          <ol className="list-decimal pl-5 space-y-1">
            {formData.instructions.some((inst) => inst.text) ? (
              formData.instructions.map(
                (inst, index) =>
                  inst.text && (
                    <li key={`preview-inst-${index}`} className="text-gray-700">
                      {inst.text}
                    </li>
                  )
              )
            ) : (
              <li className="text-gray-500 italic">
                Add instructions to see them here
              </li>
            )}
          </ol>
        </div>
      </div>
    </div>
  );
}

// Recipe List Component
function RecipeList({ recipes }) {
  if (recipes.length === 0) {
    return (
      <p className="text-gray-500 text-center italic py-6">
        No recipes found. Add your first recipe above!
      </p>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {recipes.map((recipe, index) => (
        <RecipeCard key={`recipe-${index}`} recipe={recipe} />
      ))}
    </div>
  );
}

// Recipe Card Component
function RecipeCard({ recipe }) {
  const formattedDate = new Date(recipe.created_at).toLocaleDateString();

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      <div className="p-4">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h3 className="text-xl font-semibold text-gray-800">
              {recipe.title}
            </h3>
            <div className="text-sm text-gray-500">
              By {recipe.author} | Created on {formattedDate}
            </div>
            {recipe.notes && (
              <p className="text-gray-700 mt-2">{recipe.notes}</p>
            )}
          </div>
          {recipe.image_url && (
            <img
              src={recipe.image_url}
              alt={recipe.title}
              className="w-24 h-24 object-cover rounded-md"
            />
          )}
        </div>
        <div className="flex flex-col sm:flex-row gap-6">
          <div className="flex-1">
            <h4 className="bg-gray-100 px-3 py-1 rounded-md font-medium mb-2">
              Ingredients
            </h4>
            <ul className="list-disc pl-5 space-y-1">
              {recipe.ingredients.map((ingredient, index) => (
                <li key={`ingredient-${index}`} className="text-gray-700">
                  {ingredient.quantity} {ingredient.name}
                </li>
              ))}
            </ul>
          </div>
          <div className="flex-1">
            <h4 className="bg-gray-100 px-3 py-1 rounded-md font-medium mb-2">
              Instructions
            </h4>
            <ol className="list-decimal pl-5 space-y-1">
              {recipe.instruction_steps.map((step, index) => (
                <li key={`step-${index}`} className="text-gray-700">
                  {step.instruction}
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
